import React, { FC } from 'react';
import { useRouter } from 'next/dist/client/router';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Button } from '$components/elements/button';
import { useTranslation } from '$hooks';
import { mq } from '$lib/helpers';
import { ICalculatedBasket, IShippingType } from '$models';
import { formatPrice } from '$lib/helpers';
import { B2CBasketType } from '$constants/basket';
import { Flex } from '$components/layouts';
import { checkoutPageUrl } from '$constants/urls';
import { useScrollDown } from '~/hooks/use-scroll-down';
import { useSite } from '~/store';
import { HEADER_HEIGHT_SM } from '$constants';

type BasketMobileMenuProps = {
    basket?: ICalculatedBasket;
    shipping?: IShippingType;
    onNotPossibleClick: () => void;
};

export const BasketMobileMenu: FC<BasketMobileMenuProps> = ({
    basket,
    shipping,
    onNotPossibleClick,
}: BasketMobileMenuProps): JSX.Element => {
    const router = useRouter();
    const { translate } = useTranslation();
    const scrollingDown = useScrollDown(150);
    const { headerHideable } = useSite();

    return (
        <BasketMenu
            animate={scrollingDown && headerHideable ? { translateY: `-${HEADER_HEIGHT_SM}px` } : { translateY: 0 }}
            transition={{ ease: 'easeOut' }}
        >
            <Flex justifyContent="space-between" alignItems="center" grow>
                <div>
                    <PriceLabel>{translate('checkout.orderSummary.totalPriceInclVATLabel')}</PriceLabel>
                    <div>
                        {formatPrice(
                            shipping
                                ? (basket?.totalIncVAT || 0) + (shipping?.calculatedShippingPrice || 0)
                                : basket?.totalIncVAT
                        )}
                    </div>
                </div>
                {basket?.basketType === B2CBasketType.NotPossible ? (
                    <Button onClick={onNotPossibleClick} variant="tertiary">
                        {translate('checkout.checkoutBasket.toCheckoutButton')}
                    </Button>
                ) : (
                    <Button
                        onClick={() => router.push(checkoutPageUrl)}
                        disabled={!basket?.lines?.length || !!basket.invalidLines?.length}
                    >
                        {translate('checkout.checkoutBasket.toCheckoutButton')}
                    </Button>
                )}
            </Flex>
        </BasketMenu>
    );
};

const BasketMenu = styled(motion.div)(({ theme }) => ({
    padding: `${theme.space[3]} ${theme.space[4]}`,
    backgroundColor: theme.colors.white,
    borderTop: theme.general.border,
    borderBottom: theme.general.border,
    boxSizing: 'border-box',
    position: 'fixed',
    zIndex: theme.zIndices[1],
    width: '100%',

    [mq('md')]: {
        display: 'none',
    },
}));

const PriceLabel = styled.div(({ theme }) => ({
    fontWeight: theme.fontWeights.bold,
    marginBottom: theme.space[1],
    paddingRight: theme.space[2],
}));
